import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, MenuIcon, XIcon, GlobeIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
function XLogoIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2817">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" data-id="element-2818" />
    </svg>;
}
const quickLinks = [{
  labelKey: 'footer.link.home',
  href: '#home'
}, {
  labelKey: 'footer.link.mission',
  href: '#mission'
}, {
  labelKey: 'footer.link.news',
  href: '#news'
}, {
  labelKey: 'footer.link.community',
  href: '#community'
}, {
  labelKey: 'footer.link.leadership',
  href: '#leadership'
}, {
  labelKey: 'footer.link.businessInvestments',
  href: '#business-investments'
}, {
  labelKey: 'footer.link.consular',
  href: '#consular-affairs'
}, {
  labelKey: 'footer.link.upliftingVoices',
  href: '#social-voices'
}, {
  labelKey: 'footer.link.nonprofitDirectory',
  href: '#nonprofit-directory'
}, {
  labelKey: 'footer.link.hondurasTrivia',
  href: '#honduras-trivia'
}, {
  labelKey: 'footer.link.exploreHonduras',
  href: '#explore-honduras'
}, {
  labelKey: 'footer.link.faq',
  href: '#faq'
}];
const navLinks = {
  EN: [{
    label: 'Home',
    href: '#home'
  }, {
    label: 'Mission',
    href: '#mission'
  }, {
    label: 'Leadership',
    href: '#leadership'
  }, {
    label: 'Consular Affairs',
    href: '#consular-affairs'
  }, {
    label: 'News',
    href: '#news'
  }, {
    label: 'Contact',
    href: '#contact'
  }],
  ES: [{
    label: 'Inicio',
    href: '#home'
  }, {
    label: 'Misión',
    href: '#mission'
  }, {
    label: 'Liderazgo',
    href: '#leadership'
  }, {
    label: 'Asuntos Consulares',
    href: '#consular-affairs'
  }, {
    label: 'Noticias',
    href: '#news'
  }, {
    label: 'Contacto',
    href: '#contact'
  }]
};
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const quickLinksRef = useRef<HTMLDivElement>(null);
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const {
    language,
    setLanguage,
    t,
    onLanguageSwitch
  } = useLanguage();
  // Programmatic smooth scroll to section
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const wasMobileOpen = mobileOpen;
    setMobileOpen(false);
    setQuickLinksOpen(false);
    // If we're on a sub-page (hash route like #/news/, #/consular, etc.), navigate to main page first
    const currentHash = window.location.hash;
    const isOnSubPage = currentHash.startsWith('#/');
    if (isOnSubPage) {
      // Navigate to main page, then scroll after it loads
      window.location.hash = '';
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const navbarHeight = 88;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: 'smooth'
          });
        }
      }, 500);
      return;
    }
    // If mobile menu was open, delay scroll so layout settles after menu closes
    const performScroll = () => {
      const element = document.getElementById(id);
      if (element) {
        const navbarHeight = 88; // ~5.5rem to account for fixed navbar
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - navbarHeight,
          behavior: 'smooth'
        });
      }
    };
    if (wasMobileOpen) {
      // Wait for mobile menu to collapse before calculating position
      setTimeout(performScroll, 350);
    } else {
      performScroll();
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close quick links dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (quickLinksRef.current && !quickLinksRef.current.contains(e.target as Node)) {
        setQuickLinksOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const links = navLinks[language];
  return <motion.nav initial={{
    y: -100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-blue-50 shadow-2xl' : 'bg-blue-50'}`} data-id="element-2819">
      {/* Honduran flag stripe accent: blue | white | blue */}
      <div className="h-1 w-full flex" data-id="element-2820">
        <div className="flex-1 bg-haa-blue" data-id="element-2821" />
        <div className="flex-1 bg-white" data-id="element-2822" />
        <div className="flex-1 bg-haa-blue" data-id="element-2823" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2824">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-2825">
          {/* Logo - left side on mobile */}
          <div className="flex-1 flex lg:hidden" data-id="element-2826">
            <motion.a href="#home" onClick={e => scrollToSection(e, '#home')} whileHover={{
            scale: 1.02
          }} data-id="element-2827">
              <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain" data-id="element-2828" />
            </motion.a>
          </div>

          {/* Desktop: Nav left */}
          <div className="hidden lg:flex items-center gap-1 flex-1" data-id="element-2829">
            {links.slice(0, 3).map((link, i) => <motion.a key={link.href} href={link.href} onClick={e => scrollToSection(e, link.href)} initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1 * i + 0.3
          }} className="font-nunito text-haa-navy hover:text-haa-blue px-3 py-2 rounded-md text-sm font-semibold tracking-wide transition-colors duration-200 relative group" data-id="element-2830">
                {link.label}
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-haa-blue scale-x-0 group-hover:scale-x-100 transition-transform duration-200 rounded-full" data-id="element-2831" />
              </motion.a>)}

            {/* More Dropdown - Desktop (all footer quick links) */}
            <div ref={quickLinksRef} className="relative" data-id="element-2832">
              <motion.button initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} onClick={() => setQuickLinksOpen(!quickLinksOpen)} className="font-nunito text-haa-navy hover:text-haa-blue px-3 py-2 rounded-md text-sm font-semibold tracking-wide transition-colors duration-200 relative group flex items-center gap-1" data-id="element-2833">
                {language === 'EN' ? 'More' : 'Más'}
                <motion.div animate={{
                rotate: quickLinksOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2834">
                  <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-2835" />
                </motion.div>
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-haa-blue scale-x-0 group-hover:scale-x-100 transition-transform duration-200 rounded-full" data-id="element-2836" />
              </motion.button>

              <AnimatePresence data-id="element-2837">
                {quickLinksOpen && <motion.div initial={{
                opacity: 0,
                y: 8,
                scale: 0.96
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 8,
                scale: 0.96
              }} transition={{
                duration: 0.2,
                ease: [0.16, 1, 0.3, 1]
              }} className="absolute top-full left-0 mt-2 w-56 bg-blue-50 rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-id="element-2838">
                    <div className="py-2" data-id="element-2839">
                      {quickLinks.map((link, i) => <a key={`${link.labelKey}-${i}`} href={link.href} onClick={e => scrollToSection(e, link.href)} className="flex items-center gap-2.5 px-4 py-2.5 font-nunito text-sm text-haa-navy hover:text-haa-blue hover:bg-haa-offwhite transition-colors duration-150 group/item" data-id="element-2840">
                          <ArrowRightIcon className="w-3 h-3 text-haa-blue/40 group-hover/item:text-haa-blue transition-colors flex-shrink-0" data-id="element-2841" />
                          {t(link.labelKey)}
                        </a>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Centered Logo - desktop only */}
          <motion.a href="#home" onClick={e => scrollToSection(e, '#home')} className="hidden lg:flex items-center justify-center" whileHover={{
          scale: 1.02
        }} data-id="element-2842">
            <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-14 w-auto object-contain" data-id="element-2843" />
          </motion.a>

          {/* Desktop: Nav right */}
          <div className="hidden lg:flex items-center justify-end gap-1 flex-1" data-id="element-2844">
            {links.slice(3).map((link, i) => <motion.a key={link.href} href={link.href} onClick={e => scrollToSection(e, link.href)} initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1 * (i + 3) + 0.3
          }} className="font-nunito text-haa-navy hover:text-haa-blue px-3 py-2 rounded-md text-sm font-semibold tracking-wide transition-colors duration-200 relative group" data-id="element-2845">
                {link.label}
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-haa-blue scale-x-0 group-hover:scale-x-100 transition-transform duration-200 rounded-full" data-id="element-2846" />
              </motion.a>)}
          </div>

          {/* Right side: Language toggle + Social + Mobile menu */}
          <div className="flex items-center gap-3 lg:ml-4" data-id="element-2847">
            {/* Bilingual Toggle */}
            <motion.button onClick={() => {
            if (onLanguageSwitch) {
              onLanguageSwitch();
            } else {
              setLanguage(language === 'EN' ? 'ES' : 'EN');
            }
          }} className="relative flex items-center bg-haa-navy/10 border border-haa-navy/20 rounded-full p-1 cursor-pointer hover:bg-haa-navy/20 transition-colors" whileTap={{
            scale: 0.95
          }} aria-label="Toggle language" data-id="element-2848">
              <GlobeIcon className="w-3.5 h-3.5 text-haa-navy/70 ml-1 mr-1" data-id="element-2849" />
              <div className="relative w-14 h-6 rounded-full bg-haa-navy/10 overflow-hidden" data-id="element-2850">
                <motion.div className="absolute top-0.5 w-6 h-5 rounded-full bg-haa-blue shadow-md" animate={{
                left: language === 'EN' ? '2px' : 'calc(100% - 26px)'
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-2851" />
                <div className="absolute inset-0 flex items-center justify-between px-1.5" data-id="element-2852">
                  <span className={`font-nunito text-xs font-bold z-10 ${language === 'EN' ? 'text-white' : 'text-haa-navy'}`} data-id="element-2853">
                    EN
                  </span>
                  <span className={`font-nunito text-xs font-bold z-10 ${language === 'ES' ? 'text-white' : 'text-haa-navy'}`} data-id="element-2854">
                    ES
                  </span>
                </div>
              </div>
            </motion.button>

            {/* Social Icons - Desktop */}
            <div className="hidden md:flex items-center gap-1.5" data-id="element-2855">
              {[{
              Icon: FacebookIcon,
              gradient: 'from-haa-blue to-haa-midblue',
              label: 'Facebook',
              href: '#'
            }, {
              Icon: InstagramIcon,
              gradient: 'from-haa-midblue to-haa-skyblue',
              label: 'Instagram',
              href: 'https://www.instagram.com/honduranamerican/'
            }, {
              Icon: XLogoIcon,
              gradient: 'from-haa-skyblue to-blue-400',
              label: 'X',
              href: 'https://x.com'
            }, {
              Icon: YoutubeIcon,
              gradient: 'from-haa-blue to-blue-900',
              label: 'YouTube',
              href: '#'
            }].map(({
              Icon,
              gradient,
              label,
              href
            }) => <motion.a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} whileHover={{
              scale: 1.15,
              rotate: 5
            }} whileTap={{
              scale: 0.9
            }} className={`w-7 h-7 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`} data-id="element-2856">
                  <Icon className="w-3.5 h-3.5 text-white" data-id="element-2857" />
                </motion.a>)}
            </div>

            {/* Mobile Menu Button */}
            <motion.button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-haa-navy p-2 rounded-md hover:bg-haa-navy/10 transition-colors" whileTap={{
            scale: 0.9
          }} aria-label="Toggle menu" data-id="element-2858">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-2859" /> : <MenuIcon className="w-6 h-6" data-id="element-2860" />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-2861">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="lg:hidden bg-blue-50 border-t border-gray-100 overflow-hidden shadow-xl" data-id="element-2862">
            <div className="px-4 py-4 space-y-1" data-id="element-2863">
              {/* All footer quick links as flat items */}
              {quickLinks.map((link, i) => <motion.a key={`mobile-${link.labelKey}`} href={link.href} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.03
          }} onClick={e => scrollToSection(e, link.href)} className="flex items-center gap-2.5 font-nunito font-semibold text-haa-navy hover:text-haa-blue hover:bg-haa-offwhite px-4 py-3 rounded-lg transition-colors text-base" data-id="element-2864">
                  <ArrowRightIcon className="w-3.5 h-3.5 text-haa-blue/40 flex-shrink-0" data-id="element-2865" />
                  {t(link.labelKey)}
                </motion.a>)}

              {/* Contact link */}
              <motion.a href="#contact" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: quickLinks.length * 0.03
          }} onClick={e => scrollToSection(e, '#contact')} className="flex items-center gap-2.5 font-nunito font-semibold text-haa-navy hover:text-haa-blue hover:bg-haa-offwhite px-4 py-3 rounded-lg transition-colors text-base" data-id="element-2866">
                <ArrowRightIcon className="w-3.5 h-3.5 text-haa-blue/40 flex-shrink-0" data-id="element-2867" />
                {language === 'EN' ? 'Contact' : 'Contacto'}
              </motion.a>

              {/* Mobile Social Icons */}
              <div className="flex items-center gap-3 px-4 pt-4 pb-2 border-t border-gray-100 mt-2" data-id="element-2868">
                {[{
              Icon: FacebookIcon,
              gradient: 'from-haa-blue to-haa-midblue',
              label: 'Facebook',
              href: '#'
            }, {
              Icon: InstagramIcon,
              gradient: 'from-haa-midblue to-haa-skyblue',
              label: 'Instagram',
              href: 'https://www.instagram.com/honduranamerican/'
            }, {
              Icon: XLogoIcon,
              gradient: 'from-haa-skyblue to-blue-400',
              label: 'X',
              href: 'https://x.com'
            }, {
              Icon: YoutubeIcon,
              gradient: 'from-haa-blue to-blue-900',
              label: 'YouTube',
              href: '#'
            }].map(({
              Icon,
              gradient,
              label,
              href
            }) => <a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`} data-id="element-2869">
                    <Icon className="w-4 h-4 text-white" data-id="element-2870" />
                  </a>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.nav>;
}